// LeadTimelineSidebar.tsx
// Slide-in sidebar (right-hand panel) replacing the old timeline Dialog.
// Follows the same SHELL/INNER card chrome and G/C tokens as LeadDetailView.tsx.
// Bundles the timeline together with the Quick Share (barcode) card and the
// lead's contact details, so the user never has to close the sidebar to
// check something else.

import React, { useEffect, useMemo, useState } from 'react';
import { X, Clock, IdCard, Phone, Mail, MapPin, Tag, ChevronDown, ChevronUp, Paperclip, MessageCircle } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { statusLabel } from './leadStatuses';
import {
  Lead,
  TimelineEvent,
  SHELL,
  INNER,
  Card,
  SectionHeader,
  InfoRow,
  TimelineItem,
  QuickShareCard,
  getBadgeStyle,
  getInitials,
  avc,
  SOURCE_ICONS,
} from '../LeadDetails';
import { Proposal, STATUS_VIEW, fmtDate, fmtDateTime, fmtINR, proposalApi, statusView } from '../../Proposals/parts/proposalTypes';

// ── Proposal entry on the timeline: summary + expandable sent message ─────────
const ProposalTimelineItem: React.FC<{ p: Proposal; isLast: boolean }> = ({ p, isLast }) => {
  const [open, setOpen] = useState(false);
  const sv = STATUS_VIEW[statusView(p)];
  const ChIcon = p.channel === 'email' ? Mail : MessageCircle;
  const by = p.sent_by?.email ? p.sent_by.email.split('@')[0] : null;
  const dot = C.tealMid;
  return (
    <div style={{ display: 'flex', gap: '13px', marginBottom: '3.7px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '26px', flexShrink: 0 }}>
        <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: dot, border: `2px solid ${dot}`, marginTop: '3.7px', flexShrink: 0, boxShadow: `0 0 0 3px ${dot}22` }} />
        {!isLast && <div style={{ width: '2px', flex: 1, background: `linear-gradient(${G.line}, transparent)`, minHeight: '22px', marginTop: '2.8px', marginBottom: '-3.7px' }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : '16.6px' }}>
        <div style={{ padding: '9.2px 12px', background: C.white, border: `1px solid ${G.line}`, borderLeft: `3px solid ${sv.color}`, borderRadius: '8.3px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.5px' }}>📄 Proposal sent</span>
            <span style={{ fontSize: '0.65rem', color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(p.sent_at)}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px' }}>
            <ChIcon size={12} color={p.channel === 'email' ? C.tealMid : '#128c4a'} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: '0.78rem', fontWeight: 700, color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.template_name}</span>
            <span style={{ padding: '1.8px 8px', borderRadius: '20px', fontSize: '0.6rem', fontWeight: 800, background: sv.bg, color: sv.color, border: `1px solid ${sv.border}`, flexShrink: 0 }}>{sv.label}</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 11px', fontSize: '0.66rem', color: C.sub }}>
            {typeof p.amount === 'number' && <span style={{ fontWeight: 700, color: '#1f2937' }}>{fmtINR(p.amount)}</span>}
            {p.valid_till && <span>Valid till {fmtDate(p.valid_till)}</span>}
            <span>{p.channel === 'email' ? `Emailed to ${p.to_email ?? '—'}` : `WhatsApp to ${p.to_phone ?? '—'}`}</span>
          </div>
          {by && <div style={{ marginTop: '5px', fontSize: '0.63rem', color: C.muted }}>by {by}</div>}

          <button type="button" onClick={() => setOpen((v) => !v)} style={{ marginTop: '6px', padding: '2px 0', display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'transparent', border: 'none', color: C.sub, fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.3px', cursor: 'pointer' }}>
            {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />} {open ? 'HIDE MESSAGE' : 'VIEW MESSAGE'}
          </button>

          {open && (
            <div style={{ marginTop: '6px', padding: '9px 11px', background: G.shellBg, border: `1px solid ${G.line}`, borderRadius: '7.4px' }}>
              <div style={{ fontSize: '0.64rem', color: C.muted, marginBottom: '5px' }}>{fmtDateTime(p.sent_at)}</div>
              {p.subject && <div style={{ fontSize: '0.74rem', fontWeight: 700, color: C.teal, marginBottom: '4px' }}>{p.subject}</div>}
              <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.73rem', color: '#1f2937', lineHeight: 1.55, maxHeight: '220px', overflowY: 'auto' }}>{p.body}</div>
              {p.attachments.length > 0 && (
                <div style={{ marginTop: '7px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {p.attachments.map((a) => (
                    <a key={a.path} href={a.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '20px', background: C.white, border: `1px solid ${G.line}`, color: C.teal, fontSize: '0.64rem', fontWeight: 700, textDecoration: 'none' }}>
                      <Paperclip size={10} /> {a.file_name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type Row = { key: string; date: string; ev?: TimelineEvent; proposal?: Proposal };

interface LeadTimelineSidebarProps {
  visible: boolean;
  onHide: () => void;
  lead: Lead;
  events: TimelineEvent[];
}

const LeadTimelineSidebar: React.FC<LeadTimelineSidebarProps> = ({ visible, onHide, lead, events }) => {
  // Proposals come from the proposals API (full data: status, amount, message, files).
  // They replace the plain "Proposal sent" notes; if the fetch fails the notes stay.
  const [proposals, setProposals] = useState<Proposal[] | null>(null);
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    proposalApi.lead
      .list(lead.id)
      .then((res) => !cancelled && setProposals(res.data ?? []))
      .catch(() => !cancelled && setProposals(null));
    return () => {
      cancelled = true;
    };
  }, [visible, lead.id]);

  const rows: Row[] = useMemo(() => {
    const base: Row[] = events
      .filter((ev) => !(proposals && ev.kind === 'note' && ev.note.type === 'proposal'))
      .map((ev, i) => ({ key: `e${i}`, date: ev.date, ev }));
    const extra: Row[] = (proposals ?? []).map((p) => ({ key: `p${p.id}`, date: p.sent_at, proposal: p }));
    return [...base, ...extra].sort((a, b) => (new Date(a.date).getTime() > new Date(b.date).getTime() ? -1 : 1));
  }, [events, proposals]);

  if (!visible) return null;

  const bs = getBadgeStyle(lead.status);
  const SourceIcon = SOURCE_ICONS[(lead.source || '').toLowerCase()] || Tag;
  const initials = getInitials(lead.name);
  const avatarColor = avc(lead.name || lead.email);

  return (
    <>
      <style>{`
        @keyframes ldv-sidebar-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>

      {/* Backdrop */}
      <div
        onClick={onHide}
        style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,22,0.35)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', zIndex: 1000 }}
      />

      {/* Sliding panel */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 'min(620px, 100vw)',
          background: G.shellBg,
          borderLeft: `1px solid ${G.shellBorder}`,
          boxShadow: '-8px 0 30px rgba(17,85,80,0.18)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          animation: 'ldv-sidebar-slide-in 0.22s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            ...INNER,
            borderRadius: 0,
            borderTop: 'none',
            borderRight: 'none',
            borderLeft: 'none',
            padding: '13.8px 16.6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', minWidth: 0 }}>
            <div style={{ width: '35px', height: '35px', borderRadius: '50%', background: avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0 }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6.5px' }}>
                <span style={{ fontSize: '0.93rem', fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.name}</span>
                <span style={{ padding: '1.8px 7.4px', borderRadius: '20px', fontSize: '0.6rem', fontWeight: 700, textTransform: 'capitalize', background: bs.bg, color: bs.color, border: `1px solid ${bs.border}` }}>{statusLabel(lead.status)}</span>
              </div>
              <span style={{ fontSize: '0.68rem', color: C.muted }}>{rows.length} timeline events</span>
            </div>
          </div>
          <button
            onClick={onHide}
            style={{ width: '30px', height: '30px', borderRadius: '8px', border: `1px solid ${G.line}`, background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '13.8px', display: 'flex', flexDirection: 'column', gap: '11px' }}>
          {/* <Card>
            <SectionHeader icon={IdCard} title="Contact Info" />
            <InfoRow icon={Phone} label="Phone" value={lead.phone} />
            <InfoRow icon={Mail} label="Email" value={lead.email} />
            <InfoRow icon={MapPin} label="City" value={lead.city} />
            <InfoRow icon={SourceIcon} label="Source" value={lead.source?.replace('_', ' ') || null} />
          </Card> */}

          {/* <QuickShareCard lead={lead} /> */}

          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16.6px 18.5px' }}>
              <SectionHeader icon={Clock} title="Timeline" count={rows.length} />
              {rows.map((r, idx) =>
                r.proposal ? (
                  <ProposalTimelineItem key={r.key} p={r.proposal} isLast={idx === rows.length - 1} />
                ) : (
                  <TimelineItem key={r.key} ev={r.ev!} isLast={idx === rows.length - 1} lead={lead} />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadTimelineSidebar;